import { useState } from "react";
import { Helmet } from "react-helmet";
import toast from "react-hot-toast";
import DataTable from "react-data-table-component";
import DateRangePicker from "@wojtekmaj/react-daterange-picker";

import TableLoader from "./TableLoader";
import useLeaveRequests from "../hooks/useLeaveRequests";
import SectionHeader from "./SectionHeader";
import FilterSelect from "./FilterSelect";

import leaveRequestService from "../services/leaveRequestService";

import { formatDateToSydneyTimeZone } from "../utils/dateUtils";

import leaveRequestStatuses from "../data/leaveRequestStatuses.json";

const AdminLeaveRequests = () => {
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [dateRange, setDateRange] = useState([null, null]);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const { data, isLoading, mutate } = useLeaveRequests({
    status: selectedStatus?.value || null,
    startDate: dateRange?.[0] || null,
    endDate: dateRange?.[1] || null,
    page,
    perPage,
    sortField,
    sortOrder
  });

  const leaveRequests = data?.leaveRequests;
  const leaveRequestCount = data?.count;

  const handlePageChange = page => {
    setPage(page);
  };

  const handlePerRowsChange = async (newPerPage, page) => {
    setPerPage(newPerPage);
    setPage(page);
  };

  const handleApprove = async (row) => {
    await leaveRequestService.approveLeaveRequest(row._id);
    mutate();
    toast.success('Leave request approved', { icon: '👏' });
  };

  const handleReject = async (row) => {
    await leaveRequestService.rejectLeaveRequest(row._id);
    mutate();
    toast.success('Leave request rejected', { icon: '👏' });
  };

  const handleSort = async (column, sortDirection) => {
    setSortField(column.sortField);
    setSortOrder(sortDirection === 'desc' ? 1 : -1);
  };

  const columns = [
    {
      name: "Labourer",
      selector: (row) => row.labourer.user?.fullName,
      sortable: true,
      sortField: 'labourer.user.fullName'
    },
    {
      name: "Reason",
      selector: (row) => row.reason,
      sortable: true,
      sortField: 'reason'
    },
    {
      name: "Start Date",
      selector: (row) => row.startDate,
      format: (row) => formatDateToSydneyTimeZone(row.startDate),
      sortable: true,
      sortField: 'startDate'
    },
    {
      name: "End Date",
      selector: (row) => row.endDate,
      format: (row) => formatDateToSydneyTimeZone(row.endDate),
      sortable: true,
      sortField: 'endDate'
    },
    {
      name: "Status",
      selector: (row) => row.status,
      sortable: true,
      sortField: 'status',
      conditionalCellStyles: [
        {
          when: (row) => row.status === 'Pending',
          classNames: ['text-yellow-600']
        },
        {
          when: (row) => row.status === 'Approved',
          classNames: ['text-ase-blue']
        },
        {
          when: (row) => row.status === 'Rejected',
          classNames: ['text-red-600']
        }
      ]
    },
    {
      right: "true",
      cell: row => (
        <div className='flex items-center gap-2'>
          {(row.status === 'Pending' || row.status === 'Rejected') && (
            <button
              onClick={() => handleApprove(row)}
              className='py-1 px-2 text-xs font-semibold cursor-pointer rounded-md bg-ase-blue text-white text-nowrap'
            >
              Approve
            </button>
          )}

          {(row.status === 'Pending' || row.status === 'Approved') && (
            <button
              onClick={() => handleReject(row)}
              className='py-1 px-2 text-xs font-semibold cursor-pointer rounded-md bg-red-600 text-white text-nowrap'
            >
              Reject
            </button>
          )}
        </div>
      )
    }
  ];

  return (
    <div className='border border-ase-blue rounded-lg pb-1'>
      <Helmet>
        <title>Leave requests | ASE Group</title>
      </Helmet>

      <SectionHeader
        title='Leave requests'
        actionsRight={(
          <div className='flex flex-col md:flex-row md:items-center gap-2 w-full'>
            <p className='text-white text-xs'>Status:</p>
            <div className='text-xs h-[24px]'>
              <FilterSelect
                options={leaveRequestStatuses}
                value={selectedStatus}
                onChange={setSelectedStatus}
                placeholder='Select status'
              />
            </div>

            <p className='text-white text-xs'>Period:</p>

            <DateRangePicker
              value={dateRange}
              onChange={setDateRange}
              format='dd/MM/y'
              className="filter-date-range"
            />
          </div>
        )}
      />

      <DataTable
        columns={columns}
        data={leaveRequests}
        highlightOnHover
        striped
        pointerOnHover
        sortServer
        onSort={handleSort}
        pagination
        paginationServer
        progressPending={isLoading}
        progressComponent={<TableLoader />}
        paginationTotalRows={leaveRequestCount}
        paginationRowsPerPageOptions={[10, 20, 50]}
        paginationPerPage={perPage}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handlePerRowsChange}
      />
    </div>

  );
}

export default AdminLeaveRequests;
